import { useState, type CSSProperties } from "react";

import { MONOLITH_BOARD_STYLE } from "@/components/dashboard/monolithBoardStyle";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/classNames";

const CLASSIC_FONT_FAMILY = "'Segoe UI', system-ui, -apple-system, sans-serif";
const FONDAMENTO_FONT_FAMILY = "'Fondamento', 'Palatino Linotype', serif";

const QUEST = {
  title: "Recover the Bell of Ash",
  metadata: [
    { label: "Region", value: "Ashfall Barrows" },
    { label: "Party", value: "Levels 4-6" },
    { label: "Reward", value: "Moonglass and guild favor" },
  ],
  body: [
    "A caravan bell forged from black iron was taken from the old road chapel three nights ago. The wardens believe grave-robbers plan to ring it at moonrise to pull spirits out of the peat.",
    "Travel light, question the ferryman at Candle Ford, and return with either the bell or a witness account worth filing in the guild archive.",
  ],
};

const CHROME_TABS = [
  { id: "overview", label: "Overview" },
  { id: "journal", label: "Journal" },
  { id: "archive", label: "Archive" },
] as const;

const CHROME_TAB_COPY: Record<(typeof CHROME_TABS)[number]["id"], string> = {
  overview:
    "Overview keeps the current monolith weight, but rounds the edges enough that it starts to sit closer to the lorebook shell.",
  journal:
    "Journal is the middle ground: still clearly one of our app components, but less severe than the square dashboard treatment.",
  archive:
    "Archive shows the most UI-heavy use case. This is useful if the lorebook page needs more admin tooling without fully reverting to the existing chrome.",
};

type Direction = {
  shellClassName: string;
  shellStyle?: CSSProperties;
  articleClassName: string;
  eyebrowClassName: string;
  titleClassName: string;
  metaClassName: string;
  bodyClassName: string;
  titleFontFamily: string;
  bodyFontFamily: string;
};

const CHOSEN_DIRECTION: Direction = {
  shellClassName: MONOLITH_BOARD_STYLE.dashboardHeaderPanelClassName,
  articleClassName: cn(
    "rounded-none p-6 sm:p-8",
    "border-[3px] border-neutral-950 bg-[#fffaf1] shadow-[8px_8px_0_rgba(19,19,19,0.18)]",
  ),
  eyebrowClassName: "text-[10px] font-semibold uppercase tracking-[0.28em] text-neutral-600",
  titleClassName: "mt-3 text-[2.6rem] leading-none text-neutral-950 sm:text-[3rem]",
  metaClassName: "border-2 border-neutral-950 bg-[#fff2d9] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-neutral-950",
  bodyClassName: "text-sm leading-relaxed text-neutral-800",
  titleFontFamily: FONDAMENTO_FONT_FAMILY,
  bodyFontFamily: CLASSIC_FONT_FAMILY,
};

export default function ExplorePage() {
  const [activeChromeTabId, setActiveChromeTabId] = useState<(typeof CHROME_TABS)[number]["id"]>("overview");
  const activeDirection = CHOSEN_DIRECTION;

  return (
    <div className="mx-auto min-h-full max-w-5xl px-5 py-12 pb-24 text-neutral-950 sm:px-8">
      <header className="border-b-[3px] border-neutral-950 pb-10">
        <p className="text-[11px] uppercase tracking-[0.28em] text-neutral-600">Design system - Explore</p>
        <h1 className="mt-4 text-4xl font-bold leading-tight tracking-tight [font-family:var(--color-lab-display),system-ui,sans-serif] sm:text-5xl">
          Notion lorebook
        </h1>
        <p className="mt-4 max-w-3xl text-lg leading-relaxed text-neutral-800">
          Chosen direction: old system chrome with Fondamento titles and rounded large surfaces.
        </p>
      </header>

      <section
        aria-label="Selected design direction preview"
        className={cn("mt-8 rounded-[32px] p-4 sm:p-6", activeDirection.shellClassName)}
        style={activeDirection.shellStyle}
      >
        <article className={activeDirection.articleClassName}>
          <p className={activeDirection.eyebrowClassName}>Quest / journal preview</p>
          <h2
            className={activeDirection.titleClassName}
            style={{ fontFamily: activeDirection.titleFontFamily }}
          >
            {QUEST.title}
          </h2>

          <div className="mt-5 flex flex-wrap gap-2">
            {QUEST.metadata.map((entry) => (
              <span key={entry.label} className={activeDirection.metaClassName}>
                {entry.label}: {entry.value}
              </span>
            ))}
          </div>

          <div
            className={cn("mt-5 space-y-3", activeDirection.bodyClassName)}
            style={{ fontFamily: activeDirection.bodyFontFamily }}
          >
            {QUEST.body.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </article>
      </section>

      <section className="mt-8 space-y-4" aria-labelledby="chrome-adaptations">
        <div>
          <h2
            id="chrome-adaptations"
            className="text-xl font-semibold uppercase tracking-[0.2em] [font-family:var(--color-lab-display),system-ui,sans-serif]"
          >
            Old system components
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-neutral-700">
            Current monolith component language, shown directly so the Fondamento title can be judged against the old
            system.
          </p>
        </div>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(18rem,0.9fr)]">
          <section
            className={cn(
              "rounded-[28px] p-5 sm:p-6",
              MONOLITH_BOARD_STYLE.panelClassName,
            )}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-neutral-600">Buttons</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Button type="button" variant="primary" chrome="frame" className="min-w-0!">
                Accept quest
              </Button>
              <Button type="button" variant="secondary" chrome="frame" className="min-w-0!">
                Mark in journal
              </Button>
              <Button type="button" variant="cancel" chrome="frame" className="min-w-0!">
                Close
              </Button>
            </div>

            <p className="mt-6 text-[11px] font-semibold uppercase tracking-[0.24em] text-neutral-600">Tabs</p>
            <div className="mt-4 flex flex-wrap gap-2 border-b-2 border-neutral-950/15 pb-3">
              {CHROME_TABS.map((tab) => {
                const active = tab.id === activeChromeTabId;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveChromeTabId(tab.id)}
                    className={cn(
                      "border-2 border-b-0 px-4 py-2 text-sm font-semibold transition-colors",
                      active
                        ? "border-neutral-950 bg-[#fff2d9] text-neutral-950"
                        : "border-neutral-950/15 bg-[#fffaf1] text-neutral-600 hover:bg-white",
                    )}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
            <div className="border-2 border-neutral-950 bg-[#fffaf1] p-5">
              <p className="text-sm leading-6 text-neutral-800">{CHROME_TAB_COPY[activeChromeTabId]}</p>
            </div>
          </section>

          <section
            className={cn(
              "rounded-[28px] p-5 sm:p-6",
              MONOLITH_BOARD_STYLE.panelClassName,
            )}
          >
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-neutral-600">Other pieces</p>

            <div className="mt-4 space-y-4">
              <div className="border-2 border-neutral-950/15 bg-white px-4 py-3">
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-neutral-500">Search</p>
                <p className="mt-2 text-sm text-neutral-800">Search quests, patrons, relics</p>
              </div>

              <div className="flex flex-wrap gap-2">
                <span className="border-2 border-neutral-950 bg-[#fff2d9] px-3 py-1 text-xs font-medium uppercase tracking-[0.08em] text-neutral-950">
                  Tracked
                </span>
                <span className="border-2 border-neutral-950/15 bg-white px-3 py-1 text-xs font-medium uppercase tracking-[0.08em] text-neutral-700">
                  Travel-ready
                </span>
                <span className="border-2 border-neutral-950/15 bg-white px-3 py-1 text-xs font-medium uppercase tracking-[0.08em] text-neutral-700">
                  Delicate relic
                </span>
              </div>

              <div className="border-2 border-neutral-950 bg-[#fff2d9] px-4 py-4 text-sm leading-6 text-neutral-900">
                Guild note: this is the more literal old-system control language, with the title font doing most of the
                lorebook work.
              </div>
            </div>
          </section>
        </div>
      </section>
    </div>
  );
}
